import { Button } from '@/components/ui/button';
import { FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
    Select,
    SelectContent,
    SelectGroup,
    SelectItem,
    SelectLabel,
    SelectSeparator,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { useImageUpload } from '@/db/useImageUpload';
import { cn } from '@/lib/utils';
import logger from '@/util/logger';
import {
    type ConsiderationType,
    type ConsiderationTypeId,
    type EquipmentType,
    type EquipmentTypeId,
    type EquipmentTypeIdOrContext,
    type InfoType,
    isOfferableType,
    KEY_INFO_TITLE,
} from '@rock/types';
import { groupBy, includes } from 'lodash';
import { CircleX, PlusCircle, PlusCircleIcon, XCircle } from 'lucide-react';
import { type ChangeEvent, type ComponentPropsWithoutRef, type FC, type HTMLInputTypeAttribute, useState } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import type { ControllerProps, FieldPath, FieldValues, UseFormReturn } from 'react-hook-form';
import { toast } from 'sonner';
import { v4 as uuidv4 } from 'uuid';
import { getItemTypeColors } from '../util/getItemTypeColors';

const log = logger('FormUI');

type AppInputFieldProps<
    TFieldValues extends FieldValues = FieldValues,
    TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
    TTransformedValues = TFieldValues,
> = Pick<ControllerProps<TFieldValues, TName, TTransformedValues>, 'control' | 'name'> &
    ComponentPropsWithoutRef<'div'> & {
        label?: string;
        smallLabel?: true;
        description?: string;
        placeholder?: string;
        inputType?: HTMLInputTypeAttribute;
        disabled?: boolean;
    };

export function AppInputField<T extends FieldValues = FieldValues, U extends FieldPath<T> = FieldPath<T>>({
    name,
    control,
    label,
    smallLabel,
    description,
    placeholder,
    inputType,
    disabled,
    ...rest
}: AppInputFieldProps<T, U>) {
    return (
        <FormField
            control={control}
            name={name}
            render={({ field }) => {
                // May need to add more later
                const onChange =
                    inputType === 'number'
                        ? (e: ChangeEvent<HTMLInputElement>) => field.onChange(Number.parseInt(e.target.value, 10))
                        : field.onChange;
                return (
                    <FormItem {...rest}>
                        {label && <FormLabel className={smallLabel ? 'text-xs text-gray-400' : ''}>{label}</FormLabel>}
                        <FormControl>
                            <Input
                                type={inputType}
                                className="bg-white"
                                placeholder={placeholder}
                                {...field}
                                onChange={onChange}
                                value={field.value || ''}
                                disabled={disabled}
                            />
                        </FormControl>
                        {description && <FormDescription>{description}</FormDescription>}
                        <FormMessage />
                    </FormItem>
                );
            }}
        />
    );
}

type AppTextareaFieldProps<
    TFieldValues extends FieldValues = FieldValues,
    TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
    TTransformedValues = TFieldValues,
> = Pick<ControllerProps<TFieldValues, TName, TTransformedValues>, 'control' | 'name'> &
    ComponentPropsWithoutRef<'div'> & {
        label?: string;
        smallLabel?: true;
        description?: string;
        placeholder?: string;
        disabled?: boolean;
        /** Height, in lines, to start at. The field still grows with its content. */
        rows?: number;
    };

export function AppTextareaField<T extends FieldValues = FieldValues, U extends FieldPath<T> = FieldPath<T>>({
    name,
    control,
    label,
    smallLabel,
    description,
    placeholder,
    disabled,
    rows,
    ...rest
}: AppTextareaFieldProps<T, U>) {
    return (
        <FormField
            control={control}
            name={name}
            render={({ field }) => {
                return (
                    <FormItem {...rest}>
                        {label && <FormLabel className={smallLabel ? 'text-xs text-gray-400' : ''}>{label}</FormLabel>}
                        <FormControl>
                            <Textarea
                                className={cn('bg-white', rows && 'min-h-0')}
                                rows={rows}
                                placeholder={placeholder}
                                {...field}
                                value={field.value || ''}
                                disabled={disabled}
                            />
                        </FormControl>
                        {description && <FormDescription>{description}</FormDescription>}
                        <FormMessage />
                    </FormItem>
                );
            }}
        />
    );
}

type AppPromptsFieldProps = {
    description: string;
};

/** The `prompts` field array every node form shares: a growing text box per prompt, plus its "minimal" toggle. */
export function AppPromptsField({ description }: AppPromptsFieldProps) {
    const { control } = useFormContext();
    const prompts = useFieldArray({ control, name: 'prompts' });

    return (
        <div className="flex flex-col gap-2 items-stretch group/prompts">
            <Label>Prompts</Label>
            <FormDescription>{description}</FormDescription>
            {prompts.fields.map((promptField, promptIndex) => (
                <div key={promptField.id} className="flex flex-row items-start gap-3 group/prompt">
                    <AppTextareaField
                        control={control}
                        name={`prompts.${promptIndex}.text`}
                        placeholder="Enter the prompt"
                        className="flex-1"
                        rows={1}
                    />
                    <AppCheckboxField
                        control={control}
                        name={`prompts.${promptIndex}.minimal`}
                        label="Minimal"
                        smallLabel
                    />
                    <CircleX
                        className="mt-2 cursor-pointer opacity-0 max-md:opacity-100 group-hover/prompt:opacity-100 transition-opacity fill-white hover:fill-red-300"
                        onClick={() => prompts.remove(promptIndex)}
                    />
                </div>
            ))}
            <div className="text-center">
                <Button
                    type="button"
                    onClick={() => prompts.append({ text: '' })}
                    variant="ghost"
                    size="sm"
                    className="cursor-pointer h-6 text-xs opacity-0 max-md:opacity-100 group-hover/prompts:opacity-100 transition-opacity"
                >
                    <PlusCircleIcon /> Add Prompt
                </Button>
            </div>
        </div>
    );
}

type AppSelectFieldProps<
    TFieldValues extends FieldValues = FieldValues,
    TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
    TTransformedValues = TFieldValues,
> = Pick<ControllerProps<TFieldValues, TName, TTransformedValues>, 'control' | 'name'> & {
    label?: string;
    smallLabel?: true;
    description?: string;
    placeholder?: string;
    options: SelectOption[];
    disabled?: boolean;
};

type PrimitiveSelectOption = string | number | boolean;
type SelectOption = PrimitiveSelectOption | { label: string; value: PrimitiveSelectOption };

type NormalizedSelectOption = {
    value: string;
    label: string;
    rawValue: PrimitiveSelectOption;
};

export function AppSelectField<T extends FieldValues = FieldValues, U extends FieldPath<T> = FieldPath<T>>({
    name,
    control,
    label,
    smallLabel,
    description,
    placeholder,
    options,
    disabled,
}: AppSelectFieldProps<T, U>) {
    const normalizedOptions: NormalizedSelectOption[] = options.map((option) =>
        typeof option === 'object' && option !== null && 'value' in option
            ? { value: `${option.value}`, label: option.label, rawValue: option.value }
            : { value: `${option}`, label: `${option}`, rawValue: option },
    );
    return (
        <FormField
            control={control}
            name={name}
            render={({ field }) => {
                const handleValueChange = (value: string) => {
                    if (value === 'reset') {
                        field.onChange(undefined);
                        return;
                    }
                    const selectedOption = normalizedOptions.find((option) => option.value === value);
                    field.onChange(selectedOption ? selectedOption.rawValue : value);
                };
                const stringValue =
                    field.value === undefined || field.value === null
                        ? ''
                        : typeof field.value === 'string'
                          ? field.value
                          : `${field.value}`;
                return (
                    <FormItem>
                        <FormLabel className={smallLabel ? 'text-xs text-gray-400' : ''}>{label}</FormLabel>
                        <FormControl>
                            <Select onValueChange={handleValueChange} value={stringValue} disabled={disabled}>
                                <SelectTrigger className="w-full min-w-0 bg-white">
                                    <SelectValue className="bg-white" placeholder={placeholder} />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="reset">-</SelectItem>
                                    {normalizedOptions.map((option) => (
                                        <SelectItem key={option.value} value={option.value}>
                                            {option.label}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </FormControl>
                        {description && <FormDescription>{description}</FormDescription>}
                        <FormMessage />
                    </FormItem>
                );
            }}
        />
    );
}

type AppCheckboxFieldProps<
    TFieldValues extends FieldValues = FieldValues,
    TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
    TTransformedValues = TFieldValues,
> = Pick<ControllerProps<TFieldValues, TName, TTransformedValues>, 'control' | 'name'> & {
    label?: string;
    smallLabel?: true;
    description?: string;
};

export function AppCheckboxField<T extends FieldValues = FieldValues, U extends FieldPath<T> = FieldPath<T>>({
    name,
    control,
    label,
    smallLabel,
    description,
}: AppCheckboxFieldProps<T, U>) {
    return (
        <FormField
            control={control}
            name={name}
            render={({ field }) => (
                <FormItem>
                    <FormLabel className={smallLabel ? 'text-xs text-gray-400' : ''}>{label}</FormLabel>
                    <FormControl>
                        <Switch checked={field.value} onCheckedChange={(checked) => field.onChange(checked)} />
                        {/*<Checkbox className="bg-white" checked={field.value} onCheckedChange={(checked) => field.onChange(checked)} />*/}
                    </FormControl>
                    {description && <FormDescription>{description}</FormDescription>}
                    <FormMessage />
                </FormItem>
            )}
        />
    );
}

type AppImageFieldProps<
    TFieldValues extends FieldValues = FieldValues,
    TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
    TTransformedValues = TFieldValues,
> = Pick<ControllerProps<TFieldValues, TName, TTransformedValues>, 'name'> & {
    form: UseFormReturn<TFieldValues>;
    storagePathPrefix: string;
    label?: string;
    smallLabel?: true;
    usesFullImageSchema?: boolean;
};

export function AppImageField<T extends FieldValues = FieldValues, U extends FieldPath<T> = FieldPath<T>>({
    form,
    name,
    storagePathPrefix,
    label,
    smallLabel,
    usesFullImageSchema,
}: AppImageFieldProps<T, U>) {
    const { uploadImage, progress, error } = useImageUpload();
    const [uploading, setUploading] = useState(false);
    const [selectedFileName, setSelectedFileName] = useState<string | null>(null);

    const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        setSelectedFileName(file.name);
        setUploading(true);

        try {
            const fileId = uuidv4();
            const storagePath = `${storagePathPrefix}/${fileId}`;
            const downloadURL = await uploadImage(file, storagePath);
            log.info(`Uploading image to: ${storagePath}`);

            if (!usesFullImageSchema) {
                // @ts-expect-error
                form.setValue(name, downloadURL);
                return;
            }

            /*
                Full ImageSchema has the following fields:
                    id: z.string(),
                    url: z.string(),
                    width: z.number(),
                    height: z.number(),
                    thumbnail: z.string(),
             */

            // @ts-expect-error
            form.setValue(`${name}.id`, fileId);
            // @ts-expect-error
            form.setValue(`${name}.url`, downloadURL);

            // Load image to get natural width and height
            const imgElement = await new Promise<HTMLImageElement>((resolve, reject) => {
                const reader2 = new FileReader();
                reader2.onload = (ev) => {
                    const image = new Image();
                    image.onload = () => resolve(image);
                    image.onerror = (err) => reject(err);
                    image.src = ev.target?.result as string;
                };
                reader2.onerror = (err) => reject(err);
                reader2.readAsDataURL(file);
            });
            const { width, height } = imgElement;

            // @ts-expect-error
            form.setValue(`${name}.width`, width);
            // @ts-expect-error
            form.setValue(`${name}.height`, height);

            // Generate thumbnail
            const MAX_DIMENSION = 300;
            const scale = width > height ? MAX_DIMENSION / width : MAX_DIMENSION / height;
            const thumbWidth = Math.round(width * scale);
            const thumbHeight = Math.round(height * scale);
            log.info(`Scaling image from ${width}x${height} to ${thumbWidth}x${thumbHeight} for thumbnail`);

            const canvas = document.createElement('canvas');
            canvas.width = thumbWidth;
            canvas.height = thumbHeight;
            const ctx = canvas.getContext('2d');
            if (!ctx) throw new Error('Failed to get canvas context');
            ctx.drawImage(imgElement, 0, 0, thumbWidth, thumbHeight);

            const thumbnailBlob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg'));
            if (!thumbnailBlob) throw new Error('Thumbnail creation failed');

            const thumbnailFile = new File([thumbnailBlob], `${fileId}-thumbnail.jpg`, { type: 'image/jpeg' });

            // Upload thumbnail
            const thumbStoragePath = `${storagePathPrefix}/${fileId}-tn`;
            const thumbnailUrl = await uploadImage(thumbnailFile, thumbStoragePath);

            // @ts-expect-error
            form.setValue(`${name}.thumbnail`, thumbnailUrl);
        } catch (error) {
            toast.error('Image Upload Error:', { description: `${error}` });
        } finally {
            setUploading(false);
        }
    };

    const currentValue = form.getValues(name);

    return (
        <div className="flex flex-col gap-1">
            {label && <FormLabel className={smallLabel ? 'text-xs text-gray-400' : ''}>{label}</FormLabel>}
            <div className="flex flex-col gap-1 border-1 border-dashed rounded-md p-3">
                <input type="file" accept="image/*" onChange={handleFileChange} />
                {selectedFileName && <span className="text-xs text-gray-600">{selectedFileName}</span>}
                {uploading && <div>Uploading... {progress.toFixed(0)}%</div>}
                {error && <div className="text-red-500">{error}</div>}
                {currentValue && (
                    <img
                        src={usesFullImageSchema ? currentValue.url : currentValue}
                        alt="Preview"
                        className="w-32 h-32 object-cover rounded-md border"
                    />
                )}
            </div>
        </div>
    );
}

type AppEquipmentTypeSelectFieldProps<
    TFieldValues extends FieldValues = FieldValues,
    TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = Omit<AppSelectFieldProps<TFieldValues, TName>, 'options'> & {
    equipmentTypes?: EquipmentType[];
    allowContextSelection?: boolean;
};

export function AppEquipmentTypeSelectField<
    T extends FieldValues = FieldValues,
    U extends FieldPath<T> = FieldPath<T>,
>({
    name,
    control,
    label,
    smallLabel,
    description,
    placeholder,
    equipmentTypes,
    allowContextSelection,
}: AppEquipmentTypeSelectFieldProps<T, U>) {
    return (
        <FormField
            control={control}
            name={name}
            render={({ field }) => (
                <FormItem>
                    <FormLabel className={smallLabel ? 'text-xs text-gray-400' : ''}>{label}</FormLabel>
                    <FormControl>
                        <Select
                            onValueChange={(value) => field.onChange(value === 'reset' ? undefined : value)}
                            value={field.value ? `${field.value}` : ''}
                        >
                            <SelectTrigger className="w-full min-w-0 bg-white">
                                <SelectValue className="bg-white" placeholder={placeholder} />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="reset">-</SelectItem>
                                {allowContextSelection && (
                                    <SelectGroup>
                                        <SelectLabel className="bold text-gray-500">Context</SelectLabel>
                                        <SelectSeparator />
                                        <SelectItem value="[context]" className="px-5">
                                            Use Equipment Type in Context
                                        </SelectItem>
                                    </SelectGroup>
                                )}
                                {Object.entries(offeredTypesByGroup(equipmentTypes, field.value)).map(
                                    ([group, types]) => (
                                        <SelectEquipmentTypeGroup key={group} group={group} equipmentTypes={types} />
                                    ),
                                )}
                            </SelectContent>
                        </Select>
                    </FormControl>
                    {description && <FormDescription>{description}</FormDescription>}
                    <FormMessage />
                </FormItem>
            )}
        />
    );
}

/** The types a guide may offer, grouped — plus the chosen one even when it's retired, so the value still shows. */
function offeredTypesByGroup(types: EquipmentType[] | undefined, value: unknown): Record<string, EquipmentType[]> {
    return groupBy(
        types?.filter((type) => isOfferableType(type) || type.id === value),
        (type) => type.group,
    );
}

const SelectEquipmentTypeGroup: FC<{
    group: string;
    equipmentTypes: EquipmentType[];
}> = ({ group, equipmentTypes }) => {
    if (equipmentTypes.length > 0) {
        return (
            <SelectGroup className="mt-4">
                <SelectLabel className="bold text-gray-500">{group}</SelectLabel>
                <SelectSeparator />
                {equipmentTypes.map((type) => (
                    <SelectItem key={type.id} value={type.id} className="px-5">
                        {type.title}
                    </SelectItem>
                ))}
            </SelectGroup>
        );
    }
};

type AppInfoTypeSelectFieldProps<
    TFieldValues extends FieldValues = FieldValues,
    TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = Omit<AppSelectFieldProps<TFieldValues, TName>, 'options'> & {
    infoTypes?: InfoType[];
};

export function AppInfoTypeSelectField<T extends FieldValues = FieldValues, U extends FieldPath<T> = FieldPath<T>>({
    name,
    control,
    label,
    smallLabel,
    description,
    placeholder,
    infoTypes,
}: AppInfoTypeSelectFieldProps<T, U>) {
    return (
        <FormField
            control={control}
            name={name}
            render={({ field }) => (
                <FormItem>
                    <FormLabel className={smallLabel ? 'text-xs text-gray-400' : ''}>{label}</FormLabel>
                    <FormControl>
                        <Select
                            onValueChange={(value) => field.onChange(value === 'reset' ? undefined : value)}
                            value={field.value ? `${field.value}` : ''}
                        >
                            <SelectTrigger className="w-full min-w-0 bg-white">
                                <SelectValue className="bg-white" placeholder={placeholder} />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="reset">-</SelectItem>
                                <SelectGroup className="mt-4">
                                    <SelectLabel className="bold text-gray-500">{KEY_INFO_TITLE}</SelectLabel>
                                    <SelectSeparator />
                                    {infoTypes?.map((type) => (
                                        <SelectItem key={type.id} value={type.id} className="px-5">
                                            {type.title}
                                        </SelectItem>
                                    ))}
                                </SelectGroup>
                            </SelectContent>
                        </Select>
                    </FormControl>
                    {description && <FormDescription>{description}</FormDescription>}
                    <FormMessage />
                </FormItem>
            )}
        />
    );
}

type AppEquipmentTypeMultiSelectFieldProps = {
    label: string;
    description?: string;
    equipmentTypes: EquipmentType[] | undefined;
    removeEquipmentType: (index: number) => void;
    selectedEquipmentTypesData: { fieldId: string; equipmentTypeId: EquipmentTypeIdOrContext }[];
    selectEquipmentType: (id: EquipmentTypeId) => void;
};

export function AppEquipmentTypeMultiSelectField({
    label,
    description,
    equipmentTypes,
    removeEquipmentType,
    selectedEquipmentTypesData,
    selectEquipmentType,
}: AppEquipmentTypeMultiSelectFieldProps) {
    const [open, setOpen] = useState(false);
    const typesByGroup = groupBy(equipmentTypes?.filter(isOfferableType), (t) => t.group);
    const selectedTypeIds = selectedEquipmentTypesData.map((t) => t.equipmentTypeId);
    const typesById: Record<EquipmentTypeId, EquipmentType> = {};
    for (const b of equipmentTypes || []) {
        typesById[b.id] = b;
    }

    return (
        <div className="flex flex-col gap-2">
            <Label>{label}</Label>
            {description && <FormDescription>{description}</FormDescription>}
            <div className="flex flex-wrap gap-2">
                {equipmentTypes &&
                    selectedEquipmentTypesData.map(({ fieldId, equipmentTypeId }, index) => {
                        const equipmentType = equipmentTypeId === '[context]' ? undefined : typesById[equipmentTypeId];
                        const { border, text } = getItemTypeColors('equipment');
                        return (
                            <div
                                key={fieldId}
                                className={`flex items-center border-1 ${border} ${text} bg-white pl-7 pr-2 py-1 rounded-md group/component`}
                            >
                                {equipmentType?.title || equipmentTypeId}
                                <XCircle
                                    className="ml-2 h-4 w-4 cursor-pointer text-black opacity-0 max-md:opacity-100 group-hover/component:opacity-100 fill-white hover:fill-red-300"
                                    onClick={() => removeEquipmentType(index)}
                                />
                            </div>
                        );
                    })}
            </div>

            <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger asChild>
                    <Button variant="outline" size="sm">
                        <PlusCircle className="mr-2 h-4 w-4" /> Add Equipment
                    </Button>
                </PopoverTrigger>
                <PopoverContent className="w-72">
                    <ScrollArea className="h-[200px] rounded-md">
                        {Object.keys(typesByGroup)?.map((group) => (
                            <MultiSelectEquipmentTypeGroup
                                key={group}
                                group={group}
                                equipmentTypes={typesByGroup[group]!}
                                selectedEquipmentTypeIds={selectedTypeIds}
                                onSelect={(id) => {
                                    selectEquipmentType(id);
                                    setOpen(false);
                                }}
                            />
                        ))}
                    </ScrollArea>
                </PopoverContent>
            </Popover>
        </div>
    );
}

const MultiSelectEquipmentTypeGroup: FC<{
    group: string;
    equipmentTypes: EquipmentType[];
    selectedEquipmentTypeIds: EquipmentTypeIdOrContext[];
    onSelect: (id: EquipmentTypeId) => void;
}> = ({ group, equipmentTypes, selectedEquipmentTypeIds, onSelect }) => {
    return (
        <div className="pb-2">
            <div className="py-2">
                <Label className="font-semibold">{group}</Label>
                <Separator />
            </div>
            {equipmentTypes?.map((type) => {
                const { border, text } = getItemTypeColors('equipment');

                return (
                    <Button
                        key={type.id}
                        className={`m-1 cursor-pointer ${border} ${text}`}
                        size="sm"
                        variant="outline"
                        onClick={() => onSelect(type.id)}
                        disabled={includes(selectedEquipmentTypeIds, type.id)}
                    >
                        {type.title}
                    </Button>
                );
            })}
        </div>
    );
};

type AppConsiderationTypeMultiSelectFieldProps = {
    label: string;
    description?: string;
    considerationTypes: ConsiderationType[] | undefined;
    removeConsiderationType: (index: number) => void;
    selectedConsiderationTypesData: { fieldId: string; considerationTypeId: ConsiderationTypeId }[];
    selectConsiderationType: (id: ConsiderationTypeId) => void;
};

export function AppConsiderationTypeMultiSelectField({
    label,
    description,
    considerationTypes,
    removeConsiderationType,
    selectedConsiderationTypesData,
    selectConsiderationType,
}: AppConsiderationTypeMultiSelectFieldProps) {
    const [open, setOpen] = useState(false);
    const selectedConsiderationTypeIds = selectedConsiderationTypesData.map((t) => t.considerationTypeId);
    const typesById: Record<ConsiderationTypeId, ConsiderationType> = {};
    for (const b of considerationTypes || []) {
        typesById[b.id] = b;
    }

    return (
        <div className="flex flex-col gap-2">
            <Label>{label}</Label>
            {description && <FormDescription>{description}</FormDescription>}
            <div className="flex flex-wrap gap-2">
                {considerationTypes &&
                    selectedConsiderationTypesData.map(({ fieldId, considerationTypeId }, index) => {
                        const type = typesById[considerationTypeId];
                        const { border, text } = getItemTypeColors('consideration');
                        return (
                            <div
                                key={fieldId}
                                className={`flex items-center border-1 ${border} ${text} bg-white pl-7 pr-2 py-1 rounded-md group/component`}
                            >
                                {type?.title || considerationTypeId}
                                <XCircle
                                    className="ml-2 h-4 w-4 cursor-pointer text-black opacity-0 max-md:opacity-100 group-hover/component:opacity-100 fill-white hover:fill-red-300"
                                    onClick={() => removeConsiderationType(index)}
                                />
                            </div>
                        );
                    })}
            </div>

            <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger asChild>
                    <Button variant="outline" size="sm">
                        <PlusCircle className="mr-2 h-4 w-4" /> Add Consideration
                    </Button>
                </PopoverTrigger>
                <PopoverContent className="w-72">
                    <ScrollArea className="h-[200px] rounded-md">
                        <div className="pb-2">
                            <div className="py-2">
                                <Label className="font-semibold">Consideration Types</Label>
                                <Separator />
                            </div>
                            {considerationTypes?.map((type) => {
                                const { border, text } = getItemTypeColors('consideration');

                                return (
                                    <Button
                                        key={type.id}
                                        className={`m-1 cursor-pointer ${border} ${text}`}
                                        size="sm"
                                        variant="outline"
                                        onClick={() => selectConsiderationType(type.id)}
                                        disabled={includes(selectedConsiderationTypeIds, type.id)}
                                    >
                                        {type.title}
                                    </Button>
                                );
                            })}
                        </div>
                    </ScrollArea>
                </PopoverContent>
            </Popover>
        </div>
    );
}
